import React, { useEffect, useState } from 'react';
import { apiCall } from '../utils/api';
import PdfViewer from '../components/PdfViewer';

interface SongData {
  title: string;
  artist: string;
  user_name: string;
  pdf_path: string | null;
  chart_type?: string;
  lyrics?: string | null;
}

type ViewMode = 'current' | 'next' | 'both';

function makePdfUrl(pdfPath: string | null): string | null {
  if (!pdfPath) return null;
  return `${process.env.REACT_APP_API_URL || ''}${pdfPath}`;
}

const CurrentSongLyricsPage: React.FC = () => {
  const [currentData, setCurrentData] = useState<SongData | null>(null);
  const [nextData, setNextData] = useState<SongData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('current');

  // Fetch current song lyrics
  useEffect(() => {
    let isInitialLoad = true;

    const fetchCurrent = async () => {
      if (isInitialLoad) setLoading(true);
      setError(null);
      try {
        const res = await apiCall('/api/songs/current-song-chart?instrument=vocals');
        if (!res.ok) {
          if (res.status === 404) {
            setCurrentData(prev => prev !== null ? null : prev);
            setError('No song is currently playing.');
          } else {
            throw new Error('Failed to fetch lyrics');
          }
        } else {
          const json: SongData = await res.json();
          setCurrentData(prev => {
            if (prev?.pdf_path === json.pdf_path && prev?.title === json.title) return prev;
            return json;
          });
          setError(null);
        }
      } catch (err: any) {
        setError(err.message || 'Unknown error');
      } finally {
        if (isInitialLoad) { setLoading(false); isInitialLoad = false; }
      }
    };

    fetchCurrent();
    const interval = setInterval(fetchCurrent, 5000);
    return () => clearInterval(interval);
  }, []);

  // Fetch next song lyrics when viewMode includes next
  useEffect(() => {
    if (viewMode === 'current') { setNextData(null); return; }

    const fetchNext = async () => {
      try {
        const res = await apiCall('/api/songs/next-song-chart?instrument=vocals');
        if (res.ok) {
          const json: SongData = await res.json();
          setNextData(prev => {
            if (prev?.pdf_path === json.pdf_path && prev?.title === json.title) return prev;
            return json;
          });
        } else {
          setNextData(null);
        }
      } catch { setNextData(null); }
    };

    fetchNext();
    const interval = setInterval(fetchNext, 5000);
    return () => clearInterval(interval);
  }, [viewMode]);

  const currentPdfUrl = makePdfUrl(currentData?.pdf_path ?? null);
  const nextPdfUrl = makePdfUrl(nextData?.pdf_path ?? null);

  const btnBase: React.CSSProperties = {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 'bold',
    borderRadius: '6px',
    border: '1px solid #c8850f',
    background: 'linear-gradient(180deg, #c8850f 0%, #a06800 100%)',
    color: '#fff',
    cursor: 'pointer',
    letterSpacing: '0.5px',
    textTransform: 'uppercase' as const,
  };

  const btnActive: React.CSSProperties = {
    ...btnBase,
    background: 'linear-gradient(180deg, #a06800 0%, #7a5000 100%)',
    border: '2px solid #ffd700',
  };

  const smallBtnBase: React.CSSProperties = {
    padding: '6px 10px',
    fontSize: '12px',
    fontWeight: 'bold',
    borderRadius: '4px',
    border: '1px solid #c8850f',
    background: 'linear-gradient(180deg, #c8850f 0%, #a06800 100%)',
    color: '#fff',
    cursor: 'pointer',
    width: '100%',
    letterSpacing: '0.5px',
    textTransform: 'uppercase' as const,
  };

  const smallBtnActive: React.CSSProperties = {
    ...smallBtnBase,
    background: 'linear-gradient(180deg, #a06800 0%, #7a5000 100%)',
    border: '2px solid #ffd700',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '11px', fontWeight: 'bold', color: '#c8850f',
    textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 4,
  };

  // Render a lyrics pane
  const renderLyricsPane = (data: SongData | null, url: string | null, label: string) => (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <div style={labelStyle}>{label}{data ? `: ${data.title}` : ''}</div>
      {data && (
        <p style={{ color: '#a08060', fontSize: '13px', margin: '0 0 4px 0' }}>
          {data.artist} -- {data.user_name}
        </p>
      )}
      {url ? (
        <PdfViewer src={url} title={`${label} Lyrics`} mode="paged"
          style={{ flex: 1, width: '100%', border: '1px solid #5a3a1a', borderRadius: '4px', backgroundColor: '#fff' }} />
      ) : data?.lyrics ? (
        <div style={{
          flex: 1, background: '#28190f', padding: 24, borderRadius: 8,
          border: '1px solid #5a3a1a', fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: '18px', lineHeight: '1.6', whiteSpace: 'pre-wrap', color: '#f5deb3', overflow: 'auto',
        }}>
          {data.lyrics}
        </div>
      ) : (
        <p style={{ color: '#a08060', fontStyle: 'italic', textAlign: 'center', marginTop: 20 }}>
          {label === 'Next' ? 'No next song in queue' : 'No lyrics available'}
        </p>
      )}
    </div>
  );

  // ── FULLSCREEN MODE ──
  if (!showInfo) {
    const fsLeftUrl = viewMode === 'next' ? nextPdfUrl : currentPdfUrl;
    const fsRightUrl = viewMode === 'both' ? nextPdfUrl : null;
    const hasSplit = fsRightUrl !== null;

    return (
      <div>
        <div style={{
          position: 'fixed', top: 10, right: 10, zIndex: 10000,
          backgroundColor: '#1a0f0a', padding: '12px', borderRadius: '6px',
          border: '1px solid #5a3a1a', boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
        }}>
          <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
            {(['current', 'next', 'both'] as ViewMode[]).map(mode => (
              <button key={mode} onClick={() => setViewMode(mode)}
                style={{ ...(viewMode === mode ? smallBtnActive : smallBtnBase), flex: 1, fontSize: '10px', padding: '5px 2px' }}>
                {mode === 'current' ? 'Now' : mode === 'next' ? 'Next' : 'Both'}
              </button>
            ))}
          </div>
          <button onClick={() => setShowInfo(true)} style={smallBtnBase}>
            Exit Fullscreen
          </button>
        </div>

        {hasSplit ? (
          <>
            {fsLeftUrl && <PdfViewer src={fsLeftUrl} title="Current Lyrics" mode="paged" style={{ position: 'fixed', top: 0, left: 0, width: '50vw', height: '100vh', border: 'none', zIndex: 9999, backgroundColor: '#fff' }} />}
            {fsRightUrl && <PdfViewer src={fsRightUrl} title="Next Lyrics" mode="paged" style={{ position: 'fixed', top: 0, left: '50vw', width: '50vw', height: '100vh', border: 'none', zIndex: 9999, backgroundColor: '#fff' }} />}
          </>
        ) : (
          fsLeftUrl && <PdfViewer src={fsLeftUrl} title="Lyrics Fullscreen" mode="paged" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', border: 'none', zIndex: 9999, backgroundColor: '#fff' }} />
        )}
      </div>
    );
  }

  // ── NORMAL MODE ──
  const activeData = viewMode === 'current' ? currentData : viewMode === 'next' ? nextData : null;

  return (
    <div style={{ minHeight: '100vh', padding: '32px 16px' }}>
      <h1 style={{
        color: '#ffd700', textAlign: 'center', fontSize: '28px', fontWeight: 'bold',
        marginBottom: '24px', textShadow: '0 2px 8px rgba(0,0,0,0.5)', letterSpacing: '1px',
      }}>Song Lyrics</h1>

      {/* Top controls */}
      <div style={{ textAlign: 'center', marginBottom: 16, display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => setShowInfo(false)} style={btnBase}>Fullscreen</button>

        <div style={{ display: 'flex', gap: 0 }}>
          {(['current', 'next', 'both'] as ViewMode[]).map((mode, i) => (
            <button key={mode} onClick={() => setViewMode(mode)}
              style={{
                ...(viewMode === mode ? btnActive : btnBase),
                borderRadius: i === 0 ? '6px 0 0 6px' : i === 2 ? '0 6px 6px 0' : '0',
                borderRight: i < 2 ? 'none' : undefined,
              }}>
              {mode === 'current' ? 'Current' : mode === 'next' ? 'Next' : 'Both'}
            </button>
          ))}
        </div>
      </div>

      {loading && <p style={{ color: '#a08060', textAlign: 'center', fontSize: '18px' }}>Loading lyrics...</p>}
      {error && <p style={{ color: '#e74c3c', textAlign: 'center', fontSize: '16px' }}>{error}</p>}

      {!loading && !error && (currentData || nextData) && (
        <div style={{
          maxWidth: viewMode === 'both' ? '1400px' : '900px',
          margin: '0 auto',
        }}>
          {/* Song info header — show for single view modes */}
          {viewMode !== 'both' && activeData && (
            <>
              <h2 style={{ color: '#ffd700', textAlign: 'center', fontSize: '24px', marginBottom: '4px', fontWeight: 'bold' }}>
                {activeData.title}
              </h2>
              <p style={{ color: '#a08060', textAlign: 'center', fontSize: '18px', fontStyle: 'italic', marginBottom: '4px' }}>
                {activeData.artist}
              </p>
              <p style={{ color: '#c8a060', textAlign: 'center', fontSize: '15px', marginBottom: '24px' }}>
                Requested by: {activeData.user_name}
              </p>
            </>
          )}

          {/* Lyrics content */}
          {viewMode === 'both' ? (
            <div style={{ display: 'flex', gap: 12, height: '80vh' }}>
              {renderLyricsPane(currentData, currentPdfUrl, 'Current')}
              {renderLyricsPane(nextData, nextPdfUrl, 'Next')}
            </div>
          ) : (
            (() => {
              const url = viewMode === 'current' ? currentPdfUrl : nextPdfUrl;
              const songData = viewMode === 'current' ? currentData : nextData;

              if (url) {
                return (
                  <PdfViewer src={url} title={`${songData?.title} - Lyrics`} mode="paged"
                    style={{ width: '100%', height: '80vh', border: '1px solid #5a3a1a', borderRadius: '4px', backgroundColor: '#fff' }} />
                );
              }

              if (songData?.lyrics) {
                return (
                  <div style={{
                    background: '#28190f', padding: 24, borderRadius: 8,
                    border: '1px solid #5a3a1a', boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                    fontFamily: 'system-ui, -apple-system, sans-serif', fontSize: '18px',
                    lineHeight: '1.6', whiteSpace: 'pre-wrap', color: '#f5deb3',
                  }}>
                    {songData.lyrics}
                  </div>
                );
              }

              return (
                <p style={{ color: '#a08060', textAlign: 'center', fontStyle: 'italic' }}>
                  {viewMode === 'next' ? 'No next song in queue' : 'No lyrics available for this song.'}
                </p>
              );
            })()
          )}
        </div>
      )}

      {!loading && !error && !currentData && !nextData && (
        <p style={{ color: '#a08060', textAlign: 'center', fontSize: '16px' }}>No song is currently playing.</p>
      )}
    </div>
  );
};

export default CurrentSongLyricsPage;
